import { useEffect, useState } from "react";
import { ApiError, api, type CasoDetalle, type ConfigEquipoResumen } from "@/lib/api";
import Modal from "@/ui/Modal";
import { useToast } from "@/ui/Toast";

// Minutos → texto legible (espeja domain/config_equipo.formatear_minutos).
function fmtMin(m: number): string {
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return mm === 0 ? `${h} h` : `${h} h ${mm} min`;
}

/**
 * Tiempo de configuración por equipo. Solo se monta cuando la categoría del
 * caso es `configuracion-dispositivos`. Registra cuánto tiempo del equipo de
 * soporte se invierte configurando cada equipo (IMEI), con suma por equipo y
 * total del caso. Append-only (cada sesión es un registro), espeja la UX de
 * "Registrar llamada" sin tocar ese flujo.
 */
export default function ConfiguracionEquipoPanel({
  caso,
  editable,
}: {
  caso: CasoDetalle;
  editable: boolean;
}) {
  const toast = useToast();
  const [resumen, setResumen] = useState<ConfigEquipoResumen | null>(null);
  const [loading, setLoading] = useState(true);
  const [modalImei, setModalImei] = useState<{ id: number; imei: string } | null>(null);
  const [minutos, setMinutos] = useState("");
  const [nota, setNota] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function load() {
    try {
      const r = await api.get<ConfigEquipoResumen>(
        `/api/casos/${caso.id}/configuracion-equipo`,
      );
      setResumen(r);
    } catch (err) {
      toast.error(
        "No pude cargar el tiempo de configuración",
        err instanceof ApiError ? err.detail : "Error",
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caso.id]);

  async function registrar() {
    if (!modalImei) return;
    const min = parseInt(minutos, 10);
    if (!min || min <= 0) {
      toast.error("Pon los minutos (mayor a 0)");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/api/casos/${caso.id}/configuracion-equipo`, {
        imei_id: modalImei.id,
        minutos: min,
        nota: nota.trim() || null,
      });
      toast.success("Tiempo registrado");
      setModalImei(null);
      setMinutos("");
      setNota("");
      await load();
    } catch (err) {
      toast.error("No se pudo registrar", err instanceof ApiError ? err.detail : "Error");
    } finally {
      setSubmitting(false);
    }
  }

  async function borrar(id: number) {
    try {
      await api.del(`/api/casos/${caso.id}/configuracion-equipo/${id}`);
      await load();
    } catch (err) {
      toast.error("No se pudo borrar", err instanceof ApiError ? err.detail : "Error");
    }
  }

  const porImei = resumen?.por_imei ?? {};
  const total = resumen?.total_min ?? 0;
  const registros = resumen?.registros ?? [];
  const equiposConTiempo = caso.imeis.filter((i) => (porImei[i.id] ?? 0) > 0).length;

  return (
    <div className="card space-y-3 border-emerald-500/20">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-sm font-semibold text-zinc-100">
            ⏱ Tiempo de configuración
          </h3>
          <p className="text-[11px] text-zinc-500">
            Cuánto tiempo del equipo de soporte se va configurando cada equipo de este caso.
          </p>
        </div>
        <div className="text-left sm:text-right">
          <div className="text-lg font-semibold text-emerald-300">{fmtMin(total)}</div>
          <div className="text-[10px] uppercase tracking-wide text-zinc-500">
            total · {equiposConTiempo} equipo{equiposConTiempo === 1 ? "" : "s"}
          </div>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-zinc-500">Cargando…</p>
      ) : caso.imeis.length === 0 ? (
        <p className="text-sm text-zinc-500">Este caso no tiene equipos (IMEIs) todavía.</p>
      ) : (
        <ul className="divide-y divide-ink-700">
          {caso.imeis.map((i) => {
            const sesiones = registros.filter((r) => r.imei_id === i.id);
            const sub = porImei[i.id] ?? 0;
            return (
              <li key={i.id} className="py-2 space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-sm text-zinc-200 break-all">{i.imei}</span>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-sm font-medium text-emerald-200">{fmtMin(sub)}</span>
                    {editable && (
                      <button
                        type="button"
                        onClick={() => {
                          setModalImei({ id: i.id, imei: i.imei });
                          setMinutos("");
                          setNota("");
                        }}
                        className="btn-ghost text-xs border border-ink-600"
                      >
                        + Registrar tiempo
                      </button>
                    )}
                  </div>
                </div>
                {sesiones.length > 0 && (
                  <ul className="space-y-0.5 pl-2">
                    {sesiones.map((s) => (
                      <li
                        key={s.id}
                        className="flex items-center justify-between gap-2 text-xs text-zinc-400"
                      >
                        <span className="min-w-0 break-words">
                          {fmtMin(s.minutos)} · {s.registrado_por_nombre || "—"} ·{" "}
                          {new Date(s.ocurrida_at).toLocaleDateString()}
                          {s.nota && <span className="text-zinc-500"> — {s.nota}</span>}
                        </span>
                        {editable && (
                          <button
                            type="button"
                            onClick={() => void borrar(s.id)}
                            aria-label="Borrar registro"
                            className="text-red-400/80 hover:text-red-300 flex-shrink-0"
                          >
                            ✕
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <Modal
        open={modalImei !== null}
        onOpenChange={(v) => !v && setModalImei(null)}
        title="Registrar tiempo de configuración"
        description={modalImei ? `Equipo ${modalImei.imei}` : ""}
        size="sm"
        footer={
          <>
            <button onClick={() => setModalImei(null)} className="btn-ghost text-sm">
              Cancelar
            </button>
            <button
              onClick={() => void registrar()}
              disabled={submitting}
              className="btn-primary text-sm disabled:opacity-50"
            >
              {submitting ? "Guardando…" : "Registrar"}
            </button>
          </>
        }
      >
        <div className="space-y-3">
          <label className="block space-y-1">
            <span className="text-xs text-zinc-400">Minutos</span>
            <input
              type="number"
              min={1}
              value={minutos}
              onChange={(e) => setMinutos(e.target.value)}
              autoFocus
              placeholder="ej. 45"
              className="input w-full"
            />
          </label>
          <label className="block space-y-1">
            <span className="text-xs text-zinc-400">Nota (opcional)</span>
            <textarea
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              rows={2}
              placeholder="qué se configuró…"
              className="input w-full"
            />
          </label>
        </div>
      </Modal>
    </div>
  );
}
